import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { trackPromise } from "react-promise-tracker";
import { selectUserData, selectUserToken } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { LeadsManagementEndPointLogic } from "../../../../logic/BizDevelopment/LeadsManagement/LeadsManagementEndPointLogic";
import {
  setItems,
  selectBrandMatchCache,
  selectBrandMatchLoading,
  selectBrandMatchError,
} from "../../../../features/leadsManagementSlice";
import {
  ALL_STATUS_TAB,
  BRAND_MATCH_REPORT_TO_BRAND_ENABLED,
  EMPTY_AGGREGATIONS,
  Lead,
  LeadStatus,
  LeadsAggregations,
  STATUS_TABS,
  findBrandUidForKey,
  getDefaultDateRange,
  isReportableStatus,
  isRejectableStatus,
  mapApiAggregations,
  mapApiLeadToLead,
  mapBrandMatchResponse,
  resolveStatusTab,
} from "../constants";

const SEARCH_DEBOUNCE_MS = 400;const DEFAULT_PAGE_SIZE = 10;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function isPageSizeOption(value: number): boolean {
  return PAGE_SIZE_OPTIONS.includes(value);
}

type View = "leads" | "analytics";

interface ViewResult {
  // Identifies the request this result came from — see `requestKey`.
  requestKey: string;
  leads: Lead[];
  totalCount: number;
  aggregations: LeadsAggregations;
}

// Shared empty list, so "no result yet" doesn't create a new array (and
// re-run every memo that depends on `leads`) on each render.
const NO_LEADS: Lead[] = [];
type SortField =
  | "id"
  | "name"
  | "brand"
  | "status"
  | "industry"
  | "amount"
  | "date"
  | "leadScore"
  | "approvalLikelihood"
  | "qualifiedByBrand"
  | "incomeFromBrand";
type SortDirection = "asc" | "desc";

const DEFAULTS = {
  view: "leads" as View,
  status: ALL_STATUS_TAB,
  brand: "All",
  search: "",
  page: 1,
  sortDirection: "asc" as SortDirection,
};

function isSortField(value: string | null): value is SortField {
  return (
    value === "id" ||
    value === "name" ||
    value === "brand" ||
    value === "status" ||
    value === "industry" ||
    value === "amount" ||
    value === "date" ||
    value === "leadScore" ||
    value === "approvalLikelihood" ||
    value === "qualifiedByBrand" ||
    value === "incomeFromBrand"
  );
}

// Sorting stays client-side over whatever page is currently loaded — it is
// not sent to the server, unlike pagination/status/brand/search. Returns
// null for the scoring/brand-outcome fields when the value itself is null
// (not yet scored/qualified/reported) — compareLeads always sorts those to
// the end regardless of direction, rather than treating null as 0/lowest.
function getSortValue(lead: Lead, field: SortField): string | number | null {
  switch (field) {
    case "id":
      return lead.id;
    case "date":
      return new Date(lead.date).getTime();
    case "amount":
      return Number(lead.amount.replace(/[^0-9.-]/g, "")) || 0;
    case "name":
      return lead.name.toLowerCase();
    case "brand":
      return lead.brand.toLowerCase();
    case "industry":
      return lead.industry.toLowerCase();
    case "status":
      return lead.status.toLowerCase();
    case "leadScore":
      return lead.leadScore;
    case "approvalLikelihood":
      return lead.approvalLikelihood;
    case "incomeFromBrand":
      return lead.incomeFromBrand;
    case "qualifiedByBrand":
      // Not-qualified sorts before qualified (0/1) — null (still pending a
      // brand decision) is handled as "unknown" by compareLeads below, same
      // as the other nullable fields here.
      return lead.qualifiedByBrand === null ? null : lead.qualifiedByBrand ? 1 : 0;
    default:
      return "";
  }
}

function compareLeads(a: Lead, b: Lead, field: SortField, direction: SortDirection): number {
  const aValue = getSortValue(a, field);
  const bValue = getSortValue(b, field);

  // Nulls (not yet scored/qualified/reported) always sort to the end,
  // regardless of direction — clicking "descending" shouldn't surface
  // unscored leads first just because null isn't a "high" number.
  if (aValue === null || bValue === null) {
    if (aValue === null && bValue === null) return 0;
    return aValue === null ? 1 : -1;
  }

  const result =
    typeof aValue === "number" && typeof bValue === "number"
      ? aValue - bValue
      : String(aValue).localeCompare(String(bValue));
  return direction === "asc" ? result : -result;
}

type ToastSeverity = "success" | "warning" | "error";

// The report endpoint reports each lead individually, so a single bulk
// request can come back with a mix of successes and failures.
function buildReportSummaryMessage(successCount: number, failedCount: number): string {
  const successText = `${successCount} lead${successCount === 1 ? "" : "s"} reported to brand successfully`;
  const failureText = `${failedCount} lead${failedCount === 1 ? "" : "s"} failed to report`;
  if (failedCount === 0) return successText;
  if (successCount === 0) return failureText;
  return `${successText}, ${failedCount} failed`;
}

function getReportToastSeverity(successCount: number, failedCount: number): ToastSeverity {
  if (failedCount === 0) return "success";
  if (successCount === 0) return "error";
  return "warning";
}

function buildRejectSummaryMessage(successCount: number, failedCount: number): string {
  const successText = `${successCount} lead${successCount === 1 ? "" : "s"} rejected`;
  const failureText = `${failedCount} lead${failedCount === 1 ? "" : "s"} could not be rejected`;
  if (failedCount === 0) return successText;
  if (successCount === 0) return failureText;
  return `${successText}, ${failedCount} could not be rejected`;
}

/**
 * Fetches leads from POST /partners/brand_leads_report/ for the given site.
 * Pagination, status, brand, search, qualified, min-score, and duplicate-IP
 * are all real request params — the server does the filtering/paging, this
 * hook just mirrors the current UI state into the request and reflects the
 * response back out. Every one of those params (plus page) refetches on
 * change. Column sorting stays client-side over whichever page is currently
 * loaded — the server has a documented `ordering` param but this hasn't been
 * wired up to it yet.
 *
 * Analytics' KPI cards/charts read from the response's `aggregations` object
 * (by_status/by_day/by_brand/qualified_by_brand/...), not from the paginated
 * `leads` page — so they reflect the full matching set for the current
 * brand/date filters. search/status/qualified/min-score/duplicate-IP are all
 * Leads-tab-only concepts with no Analytics-tab UI, so they're only sent to
 * the server when `view === "leads"` — otherwise they'd silently narrow
 * Analytics' aggregations too.
 *
 * view/status/brand/analytics_brand/start/end/analytics_start/analytics_end/search/page/
 * sort/dir/qualified/ip/min_score are all mirrored to the URL query string (replacing history, not pushing)
 * so sharing the URL or refreshing the page reproduces the same view.
 */
export function useLeadsManagementData(websiteSharedUid: string) {
  const dispatch = useDispatch();
  const userToken = useSelector(selectUserToken);
  const userData = useSelector(selectUserData);
  const canReportLeads = CheckPermissions(
    userData?.data?.products?.partners,
    "lead_managment",
    "edit_lead_managment",
  );
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [searchParams, setSearchParams] = useSearchParams();

  // Each tab keeps the result of its last fetch, tagged with the request it
  // came from. Switching back to a tab whose request hasn't changed shows
  // that result straight away without hitting the API again — the Refresh
  // button is there for a manual reload.
  const [resultsByView, setResultsByView] = useState<Partial<Record<View, ViewResult>>>({});
  // The request currently being fetched (null when idle). Used to show the
  // loading state during a manual refresh or a refetch after an action.
  const [inFlightRequestKey, setInFlightRequestKey] = useState<string | null>(null);
  const [view, setView] = useState<View>(
    () => (searchParams.get("view") === "analytics" ? "analytics" : "leads"),
  );
  // Each tab keeps its own brand selection, same as its date range below —
  // picking a brand on one tab never filters the other. `brand`/`setBrand`
  // always point at the open tab's selection.
  const [leadsBrand, setLeadsBrand] = useState(() => searchParams.get("brand") ?? DEFAULTS.brand);
  const [analyticsBrand, setAnalyticsBrand] = useState(
    () => searchParams.get("analytics_brand") ?? DEFAULTS.brand,
  );
  const brand = view === "analytics" ? analyticsBrand : leadsBrand;
  const setBrand = view === "analytics" ? setAnalyticsBrand : setLeadsBrand;
  // Accumulates {brand_uid: brand_name} pairs seen across fetches so the
  // dropdown keeps showing brands the user has already filtered away from.
  // There's no dedicated "list all brands for this site" endpoint, so this
  // directory only ever knows about brands that have shown up in a response.
  const [brandDirectory, setBrandDirectory] = useState<Record<string, string>>({});
  // Mirrors brandDirectory for reportToBrand's re-check after
  // ensureBrandDirectory() resolves — the `brandDirectory` closure variable
  // captured before that await would still be the stale pre-fetch value.
  // Written directly inside mergeBrandDirectory's setState updater below
  // (not via a separate useEffect keyed on brandDirectory), so it's never a
  // render/commit cycle behind the state it mirrors.
  const brandDirectoryRef = useRef<Record<string, string>>({});
  // Each tab keeps its own date range, so drilling into the Leads tab (e.g.
  // clicking a day on the Leads by Day chart) never changes the range the
  // Analytics tab is showing. `start`/`end`/`setStart`/`setEnd` always
  // point at the range of whichever tab is currently open.
  const [leadsStart, setLeadsStart] = useState(() => searchParams.get("start") ?? defaultRange.from);
  const [leadsEnd, setLeadsEnd] = useState(() => searchParams.get("end") ?? defaultRange.to);
  const [analyticsStart, setAnalyticsStart] = useState(
    () => searchParams.get("analytics_start") ?? defaultRange.from,
  );
  const [analyticsEnd, setAnalyticsEnd] = useState(
    () => searchParams.get("analytics_end") ?? defaultRange.to,
  );
  const isAnalyticsView = view === "analytics";
  const start = isAnalyticsView ? analyticsStart : leadsStart;
  const end = isAnalyticsView ? analyticsEnd : leadsEnd;
  const setStart = isAnalyticsView ? setAnalyticsStart : setLeadsStart;
  const setEnd = isAnalyticsView ? setAnalyticsEnd : setLeadsEnd;
  const [search, setSearch] = useState(() => searchParams.get("search") ?? DEFAULTS.search);
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [status, setStatus] = useState<string>(() => resolveStatusTab(searchParams.get("status")));
  // Qualified/IP/min-score map to the server's qualified_by_brand/
  // has_duplicate_ip/lead_scoring_min request params (Leads-tab-only, see
  // fetchLeads below). ipFilter's values are "All" | "Shared" | "Unique" —
  // has_duplicate_ip is a shared-vs-unique boolean, not a specific IP value;
  // "view leads from this exact IP" goes through the `search` param instead
  // (see goToFilteredLeads).
  const [qualified, setQualifiedState] = useState(() => searchParams.get("qualified") ?? "All");
  const [ipFilter, setIpFilterState] = useState(() => searchParams.get("ip") ?? "All");
  const [minScore, setMinScoreState] = useState(() => searchParams.get("min_score") ?? "0");
  // Maps to the reported_to_multiple_brands request param — same
  // All/Yes/No shape as qualified/ipFilter above.
  const [multiBrand, setMultiBrandState] = useState(() => searchParams.get("multi_brand") ?? "All");
  const [sortField, setSortField] = useState<SortField | null>(() => {
    const param = searchParams.get("sort");
    return isSortField(param) ? param : null;
  });
  const [sortDirection, setSortDirection] = useState<SortDirection>(
    () => (searchParams.get("dir") === "desc" ? "desc" : DEFAULTS.sortDirection),
  );
  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [page, setPage] = useState(() => Number(searchParams.get("page")) || DEFAULTS.page);
  const [perPage, setPerPageState] = useState(() => {
    const param = Number(searchParams.get("page_size"));
    return isPageSizeOption(param) ? param : DEFAULT_PAGE_SIZE;
  });
  const [detailId, setDetailId] = useState<number | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<ToastSeverity>("success");
  const [isReporting, setIsReporting] = useState(false);
  const [showRejectConfirm, setShowRejectConfirm] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [matchLeadId, setMatchLeadId] = useState<number | null>(null);
  // Separate from matchLeadId so the dialog's content stays populated through
  // its closing animation — nulling matchLeadId on click would blank the
  // header/body while the dialog is still fading out, causing a flicker.
  const [matchModalOpen, setMatchModalOpen] = useState(false);
  // Which brand key is currently being reported to (null when idle) — drives
  // the per-brand "processing" state in the diagnostic modal and guards
  // against firing a second report while one is in flight.
  const [reportingBrandKey, setReportingBrandKey] = useState<string | null>(null);
  const brandMatchCache = useSelector(selectBrandMatchCache);
  const brandMatchLoading = useSelector(selectBrandMatchLoading);
  const brandMatchError = useSelector(selectBrandMatchError);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [search]);
  // The exact request(s) the open tab needs. Multi-status tabs (e.g.
  // Internal Duplication) have no single report_status to send, so they're
  // fetched as one request per status.
  const requestParamsList = useMemo(() => {
    const activeTab = STATUS_TABS.find((tab) => tab.key === status);
    const statusesToFetch: (LeadStatus | undefined)[] =
      view === "leads" && activeTab ? activeTab.statuses : [undefined];

    return statusesToFetch.map((reportStatus) => ({
      website_shared_uid: [websiteSharedUid],
      date_from: start,
      date_to: end,
      page,
      page_size: perPage,
      // Analytics has no search box of its own (only Brand/Start/End), so a
      // search term typed on the Leads tab must not silently narrow its
      // aggregations too — same reasoning as report_status below.
      search: view === "leads" ? debouncedSearch || undefined : undefined,
      report_status: reportStatus,
      brand_uid: brand !== "All" ? [brand] : undefined,
      // qualified_by_brand/lead_scoring_min/has_duplicate_ip: proposed by
      // the backend, not yet confirmed live (see the "Extending POST
      // .../brand_leads_report/" spec's Important Notes). Omitted (not
      // null/"") when unset, matching brand_uid/report_status above.
      // Leads-tab-only, same reasoning as report_status/search.
      qualified_by_brand:
        view === "leads" && qualified !== "All" ? qualified === "Yes" : undefined,
      lead_scoring_min: view === "leads" && minScore !== "0" ? Number(minScore) : undefined,
      has_duplicate_ip:
        view === "leads" && ipFilter !== "All" ? ipFilter === "Shared" : undefined,
      reported_to_multiple_brands:
        view === "leads" && multiBrand !== "All" ? multiBrand === "Yes" : undefined,
    }));
  }, [
    websiteSharedUid,
    start,
    end,
    page,
    perPage,
    debouncedSearch,
    status,
    brand,
    qualified,
    minScore,
    ipFilter,
    multiBrand,
    view,
  ]);

  // Changes whenever anything that affects the open tab's request changes.
  const requestKey = JSON.stringify(requestParamsList);

  const currentResult = resultsByView[view];
  const hasFreshResult = currentResult?.requestKey === requestKey;
  const leads = currentResult?.leads ?? NO_LEADS;
  const totalCount = currentResult?.totalCount ?? 0;
  const aggregations = currentResult?.aggregations ?? EMPTY_AGGREGATIONS;
  // Loading while the open tab's data doesn't match its current request
  // (it's about to be fetched), or while that request is being fetched.
  const isLoading =
    (!hasFreshResult && Boolean(start && end)) || inFlightRequestKey === requestKey;

  // Only the most recent fetch may update state — e.g. after a quick tab
  // switch, the previous tab's slower response must not overwrite newer data.
  const latestFetchIdRef = useRef(0);

  const fetchLeads = useCallback(() => {
    if (!userToken || !start || !end) return;
    const fetchId = ++latestFetchIdRef.current;
    const isLatestFetch = () => fetchId === latestFetchIdRef.current;
    // Captured now, so the result is stored against the tab and request it
    // was actually fetched for, even if the user has moved on since.
    const fetchedView = view;
    const fetchedRequestKey = requestKey;
    setInFlightRequestKey(fetchedRequestKey);

    const storeResult = (result: Omit<ViewResult, "requestKey">) =>
      setResultsByView((current) => ({
        ...current,
        [fetchedView]: { requestKey: fetchedRequestKey, ...result },
      }));

    // A null response means that status's request failed; the .catch here
    // ensures it never skips the merge below and leaves stale leads on screen.
    const requests = requestParamsList.map((params) => {
      let result: any = null;
      return LeadsManagementEndPointLogic.getLeadsContent(
        userToken,
        params,
        (data: any) => {
          result = data;
        },
        trackPromise,
      )
        .then(() => result)
        .catch(() => null);
    });

    return Promise.all(requests)
      .then((responses) => {
        if (!isLatestFetch()) return;
        const successfulResponses = responses.filter((data) => data !== null);
        const mappedLeads: Lead[] = successfulResponses.flatMap((data) =>
          (data.results ?? []).map(mapApiLeadToLead),
        );
        const totalFromResponses = successfulResponses.reduce(
          (sum, data) => sum + (typeof data.count === "number" ? data.count : 0),
          0,
        );
        const mappedAggregations = mapApiAggregations(successfulResponses[0]?.aggregations);
        storeResult({
          leads: mappedLeads,
          totalCount: totalFromResponses,
          aggregations: mappedAggregations,
        });
        // aggregations.by_brand is scoped by report_status — a single-status
        // tab's fetch only ever surfaces brands present under that one
        // status, never the full set. This merge is just an opportunistic,
        // free top-up from whatever this fetch happened to return; the
        // authoritative, filter-independent source is ensureBrandDirectory
        // below.
        mergeBrandDirectory(
          mappedAggregations.byBrand.map((brand) => ({
            brandUid: brand.brandUid,
            brand: brand.brandName,
          })),
        );
        dispatch(setItems(mappedLeads));
      })
      .catch(() => {
        if (!isLatestFetch()) return;
        // Stored (not left empty) so the tab stops loading — the Refresh
        // button retries.
        storeResult({ leads: [], totalCount: 0, aggregations: EMPTY_AGGREGATIONS });
      })
      .finally(() => {
        if (isLatestFetch()) setInFlightRequestKey(null);
      });
  }, [userToken, start, end, view, requestKey, requestParamsList, dispatch]);

  // Fetches only when the open tab has no result for its current request —
  // i.e. something changed. Switching back to an unchanged tab reuses its
  // last result.
  useEffect(() => {
    if (!hasFreshResult) fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestKey, view, userToken]);

  // Reporting/rejecting changes lead statuses server-side, so the other
  // tab's saved result is out of date — drop it so it refetches when
  // opened — and refetch the open tab.
  function refetchAfterLeadsChanged() {
    setResultsByView((current) => ({ [view]: current[view] }));
    fetchLeads();
  }

  // Applies an immediate local edit to the open tab's leads (before the
  // follow-up refetch lands).
  function updateCurrentLeads(update: (current: Lead[]) => Lead[]) {
    setResultsByView((current) => {
      const result = current[view];
      if (!result) return current;
      return { ...current, [view]: { ...result, leads: update(result.leads) } };
    });
  }

  // brandUid empty-string entries are dropped defensively — aggregations.by_brand
  // shouldn't have them, but an empty key would otherwise pollute the
  // brand_uid -> name directory used for both the Brand dropdown and
  // reportToBrand's lookup.
  function mergeBrandDirectory(entries: Array<{ brandUid: string; brand: string }>) {
    setBrandDirectory((previous) => {
      const next = { ...previous };
      entries.forEach(({ brandUid, brand }) => {
        if (brandUid) next[brandUid] = brand;
      });
      // DEBUG — remove once brand_uid resolution is confirmed reliable.
      // Logs only when this call actually adds a brand_uid the directory
      // didn't already have, so it's easy to see which call (the per-tab
      // fetch, or the dedicated ensureBrandDirectory fetch below) taught the
      // directory about a given brand.
      const addedEntries = Object.entries(next).filter(([uid]) => previous[uid] === undefined);
      if (addedEntries.length > 0) {
        console.log("[brandDirectory] new brand_uid(s) learned:", addedEntries);
      }
      brandDirectoryRef.current = next;
      return next;
    });
  }

  // aggregations.by_brand is scoped by report_status, so no single-status
  // tab's fetch can ever see the full brand set. This dedicated fetch omits
  // report_status entirely to get the broadest by_brand list available for
  // the date range. It's deduped per date range so repeat/concurrent callers
  // share one request instead of firing their own. page_size is kept
  // minimal since only the response's aggregations are used, never its
  // `results`.
  const brandDirectoryLoadedRangeRef = useRef<string | null>(null);
  const brandDirectoryPromiseRef = useRef<Promise<void> | null>(null);

  function ensureBrandDirectory(): Promise<void> {
    if (!userToken || !start || !end) return Promise.resolve();
    const rangeKey = `${start}|${end}`;
    if (brandDirectoryLoadedRangeRef.current === rangeKey) return Promise.resolve();
    if (brandDirectoryPromiseRef.current) return brandDirectoryPromiseRef.current;

    const promise = LeadsManagementEndPointLogic.getLeadsContent(
      userToken,
      {
        website_shared_uid: [websiteSharedUid],
        date_from: start,
        date_to: end,
        page: 1,
        page_size: 1,
      },
      (data: any) => {
        const aggregations = mapApiAggregations(data?.aggregations);
        mergeBrandDirectory(
          aggregations.byBrand.map((brand) => ({ brandUid: brand.brandUid, brand: brand.brandName })),
        );
      },
      trackPromise,
    )
      .then(() => {
        brandDirectoryLoadedRangeRef.current = rangeKey;
      })
      .finally(() => {
        brandDirectoryPromiseRef.current = null;
      });

    brandDirectoryPromiseRef.current = promise;
    return promise;
  }

  // Fires ensureBrandDirectory exactly once, as soon as the module is
  // entered and userToken/start/end are ready — not on every date-range
  // change (that was the earlier eager-effect version this replaced), and
  // not deferred to a dropdown open. reportToBrand still calls
  // ensureBrandDirectory itself as a fallback if a brand isn't resolvable
  // yet; the caching above means that call is a no-op once this has run.
  const hasFetchedBrandDirectoryOnEntryRef = useRef(false);
  useEffect(() => {
    if (hasFetchedBrandDirectoryOnEntryRef.current) return;
    if (!userToken || !start || !end) return;
    hasFetchedBrandDirectoryOnEntryRef.current = true;
    ensureBrandDirectory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken, start, end]);

  const brandOptions = useMemo(() => {
    const entries = Object.entries(brandDirectory).sort((a, b) => a[1].localeCompare(b[1]));
    return [
      { value: "All", label: "All brands" },
      ...entries.map(([uid, name]) => ({ value: uid, label: name })),
    ];
  }, [brandDirectory]);

  // income_by_brand rows only carry a brand name (no brand_uid), so the
  // Income by Brand click-through looks the uid up in the brand directory.
  function getBrandUidByName(brandName: string): string | undefined {
    return Object.entries(brandDirectory).find(([, name]) => name === brandName)?.[0];
  }

  const sortedLeads = useMemo(() => {
    if (!sortField) return leads;
    return [...leads].sort((a, b) => compareLeads(a, b, sortField, sortDirection));
  }, [leads, sortField, sortDirection]);

  const totalFiltered = totalCount;
  const totalPages = Math.max(1, Math.ceil(totalCount / perPage));
  const currentPage = Math.min(page, totalPages);
  const pageStartIndex = (currentPage - 1) * perPage;
  const pageLeads = sortedLeads;

  function toggleSort(field: SortField) {
    if (sortField === field) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  }

  // Mirror the current filters/tab/page/sort to the URL, preserving any other
  // query params already present, without piling up history entries.
  useEffect(() => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        const setOrClear = (key: string, value: string, defaultValue: string) => {
          if (value && value !== defaultValue) next.set(key, value);
          else next.delete(key);
        };
        setOrClear("view", view, DEFAULTS.view);
        setOrClear("status", status, DEFAULTS.status);
        setOrClear("brand", leadsBrand, DEFAULTS.brand);
        setOrClear("analytics_brand", analyticsBrand, DEFAULTS.brand);
        setOrClear("start", leadsStart, defaultRange.from);
        setOrClear("end", leadsEnd, defaultRange.to);
        setOrClear("analytics_start", analyticsStart, defaultRange.from);
        setOrClear("analytics_end", analyticsEnd, defaultRange.to);
        setOrClear("search", search, DEFAULTS.search);
        setOrClear("page", String(currentPage), String(DEFAULTS.page));
        setOrClear("page_size", String(perPage), String(DEFAULT_PAGE_SIZE));
        setOrClear("sort", sortField ?? "", "");
        setOrClear("dir", sortField ? sortDirection : "", DEFAULTS.sortDirection);
        setOrClear("qualified", qualified, "All");
        setOrClear("ip", ipFilter, "All");
        setOrClear("min_score", minScore, "0");
        setOrClear("multi_brand", multiBrand, "All");
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    status,
    leadsBrand,
    analyticsBrand,
    leadsStart,
    leadsEnd,
    analyticsStart,
    analyticsEnd,
    search,
    currentPage,
    perPage,
    sortField,
    sortDirection,
    qualified,
    ipFilter,
    minScore,
    multiBrand,
  ]);

  // Select-all only ever covers the current (server-paginated) page — the
  // same convention as select-all in any other paginated table. A row is
  // selectable if either bulk action could apply to it — isRejectableStatus
  // is the wider of the two (every reportable status is also rejectable).
  const selectableOnPage = useMemo(
    () => leads.filter((lead) => isRejectableStatus(lead.status)),
    [leads],
  );
  const selectedCount = Object.keys(selected).length;
  const allChecked =
    selectableOnPage.length > 0 && selectableOnPage.every((lead) => selected[lead.id]);

  const detailLead = leads.find((lead) => lead.id === detailId) ?? null;

  const selectedLeads = leads.filter((lead) => selected[lead.id]);
  // Reject is enabled when every selected row is in a rejectable status.
  const allSelectedRejectable =
    selectedCount > 0 && selectedLeads.every((lead) => isRejectableStatus(lead.status));
  const allSelectedReportable =
    selectedCount > 0 && selectedLeads.every((lead) => isReportableStatus(lead.status));

  const matchLead = leads.find((lead) => lead.id === matchLeadId) ?? null;
  const matchRawResult = matchLeadId !== null ? brandMatchCache[matchLeadId] ?? null : null;
  const matchDiagnostic = useMemo(
    () =>
      matchRawResult
        ? mapBrandMatchResponse(matchRawResult, matchLead?.brand ?? "")
        : null,
    [matchRawResult, matchLead],
  );

  function goToFilteredLeads(
    overrides: Partial<{
      status: string;
      start: string;
      end: string;
      minScore: number;
      qualified: string;
      search: string;
      // A brand_uid for the Leads tab's Brand Name filter.
      brand: string;
    }>,
  ) {
    // Only the Leads tab's filters change — Analytics keeps its own. Coming
    // from Analytics, the Leads tab opens on the brand and range Analytics
    // is showing, so its list matches the number that was clicked. Coming
    // from the Leads tab itself (e.g. "see leads from this IP"), the range
    // stays as it is and the brand filter is cleared.
    const fromAnalytics = view === "analytics";
    setView("leads");
    setStatus(resolveStatusTab(overrides.status));
    setLeadsBrand(overrides.brand ?? (fromAnalytics ? analyticsBrand : DEFAULTS.brand));
    setLeadsStart(overrides.start ?? (fromAnalytics ? analyticsStart : leadsStart));
    setLeadsEnd(overrides.end ?? (fromAnalytics ? analyticsEnd : leadsEnd));
    setQualifiedState(overrides.qualified ?? "All");
    // has_duplicate_ip can't express "leads sharing this exact IP" (it's a
    // shared-vs-unique boolean, not a value filter) — "view leads from this
    // IP" goes through `search` instead, which the server matches against
    // ip_address too (see the request-param spec).
    setIpFilterState("All");
    setMinScoreState(overrides.minScore !== undefined ? String(overrides.minScore) : "0");
    setMultiBrandState("All");
    setSearch(overrides.search ?? "");
    setPage(1);
    setSelected({});
    setDetailId(null);
  }

  function toggleLeadSelected(leadId: number) {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || !isRejectableStatus(lead.status)) return;
    setSelected((current) => {
      const next = { ...current };
      if (next[leadId]) delete next[leadId];
      else next[leadId] = true;
      return next;
    });
  }

  // Single-lead reject (mobile detail drawer): the selection becomes exactly
  // this lead, so a stale selection from a cancelled attempt can't drop it
  // (toggle) or ride along with it into the reject request.
  function requestRejectLead(leadId: number) {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || !isRejectableStatus(lead.status)) return;
    setSelected({ [leadId]: true });
    setShowRejectConfirm(true);
  }

  // Single-lead report with confirmation (mobile detail drawer) — same
  // exact-selection approach as requestRejectLead.
  function requestReportLead(leadId: number) {
    const lead = leads.find((l) => l.id === leadId);
    if (!lead || !isReportableStatus(lead.status)) return;
    setSelected({ [leadId]: true });
    setShowConfirm(true);
  }

  function toggleAllSelectable() {
    setSelected((current) => {
      const next = { ...current };
      if (allChecked) {
        selectableOnPage.forEach((lead) => delete next[lead.id]);
      } else {
        selectableOnPage.forEach((lead) => (next[lead.id] = true));
      }
      return next;
    });
  }

  function clearSelection() {
    setSelected({});
  }

  function markLeadsReported(leadIds: number[]) {
    const reportedAt = new Date().toISOString();
    updateCurrentLeads((current) =>
      current.map((lead) =>
        leadIds.includes(lead.id) ? { ...lead, status: "Success" as LeadStatus, reportedAt } : lead,
      ),
    );
  }

  async function reportSingleLead(leadId: number) {
    if (!userToken || isReporting) return;
    setIsReporting(true);
    const results = await LeadsManagementEndPointLogic.reportLeadsToBrand(
      userToken,
      [leadId],
      trackPromise,
    );
    setIsReporting(false);
    if (!results) return;
    // On a hard request failure, the error snackbar is already shown by
    // reportLeadsToBrand — keep the drawer open so the user can retry.
    const succeededIds = results.filter((result) => result.succeeded).map((result) => result.leadId);
    const failedCount = results.length - succeededIds.length;
    if (succeededIds.length > 0) markLeadsReported(succeededIds);
    setDetailId(null);
    setToastMessage(buildReportSummaryMessage(succeededIds.length, failedCount));
    setToastSeverity(getReportToastSeverity(succeededIds.length, failedCount));
    refetchAfterLeadsChanged();
  }

  async function confirmBulkReport() {
    if (!userToken || isReporting) return;
    const reportableSelectedIds = selectedLeads
      .filter((lead) => isReportableStatus(lead.status))
      .map((lead) => lead.id);
    if (reportableSelectedIds.length === 0) {
      setShowConfirm(false);
      return;
    }
    setIsReporting(true);
    const results = await LeadsManagementEndPointLogic.reportLeadsToBrand(
      userToken,
      reportableSelectedIds,
      trackPromise,
    );
    setIsReporting(false);
    if (!results) return;
    // On a hard request failure, the error snackbar is already shown by
    // reportLeadsToBrand — keep the confirm dialog and selection intact so
    // the user can retry.
    const succeededIds = results.filter((result) => result.succeeded).map((result) => result.leadId);
    const failedCount = results.length - succeededIds.length;
    if (succeededIds.length > 0) markLeadsReported(succeededIds);
    setSelected({});
    setShowConfirm(false);
    setToastMessage(buildReportSummaryMessage(succeededIds.length, failedCount));
    setToastSeverity(getReportToastSeverity(succeededIds.length, failedCount));
    refetchAfterLeadsChanged();
  }

  function markLeadsRejected(leadIds: number[]) {
    updateCurrentLeads((current) =>
      current.map((lead) =>
        leadIds.includes(lead.id) ? { ...lead, status: "Rejected" as LeadStatus } : lead,
      ),
    );
  }

  async function confirmBulkReject() {
    if (!userToken || isRejecting) return;
    const rejectableSelectedIds = selectedLeads
      .filter((lead) => isRejectableStatus(lead.status))
      .map((lead) => lead.id);
    if (rejectableSelectedIds.length === 0) {
      setShowRejectConfirm(false);
      return;
    }
    setIsRejecting(true);
    const result = await LeadsManagementEndPointLogic.rejectLeads(
      userToken,
      rejectableSelectedIds,
      trackPromise,
    );
    setIsRejecting(false);
    if (!result) return;
    if (result.updatedIds.length > 0) markLeadsRejected(result.updatedIds);
    setSelected({});
    setShowRejectConfirm(false);
    setToastMessage(buildRejectSummaryMessage(result.updatedIds.length, result.failedCount));
    setToastSeverity(getReportToastSeverity(result.updatedIds.length, result.failedCount));
    refetchAfterLeadsChanged();
  }

  function openBrandMatch(leadId: number) {
    setMatchLeadId(leadId);
    setMatchModalOpen(true);
    if (!userToken) return;
    const leadIdKey = String(leadId);
    // Cache hit resolves synchronously inside getBrandMatchData — no loading
    // flicker for a lead whose match data was already fetched this session.
    LeadsManagementEndPointLogic.getBrandMatchData(
      userToken,
      websiteSharedUid,
      leadIdKey,
      false,
      trackPromise,
    );
  }

  function closeBrandMatch() {
    setMatchModalOpen(false);
  }

  // Called once the dialog's closing animation actually finishes (wired to
  // Dialog's TransitionProps.onExited) — only then is it safe to drop the
  // lead reference without the content blanking mid-fade.
  function handleMatchModalExited() {
    setMatchLeadId(null);
  }

  // Manually reassigns + reports the currently open diagnostic's lead to
  // brandKey (a Brand Match query brand key, e.g. "Lendzi"), resolved to a
  // brand_uid via the brand directory. Gated by BRAND_MATCH_REPORT_TO_BRAND_ENABLED
  // and by the lead's status — a lead already reported ("Success") has no
  // report action exposed in the modal (see BrandMatchModal's canReport).
  async function reportToBrand(brandKey: string) {
    if (!BRAND_MATCH_REPORT_TO_BRAND_ENABLED || !canReportLeads) return;
    if (!userToken || matchLeadId === null || reportingBrandKey) return;

    let brandUid = findBrandUidForKey(brandDirectory, brandKey);
    if (!brandUid) {
      await ensureBrandDirectory();
      brandUid = findBrandUidForKey(brandDirectoryRef.current, brandKey);
    }
    if (!brandUid) {
      setToastMessage(`Could not resolve a brand_uid for ${brandKey} — try re-running the query.`);
      setToastSeverity("error");
      return;
    }

    setReportingBrandKey(brandKey);
    try {
      const result = await LeadsManagementEndPointLogic.reassignAndReportLead(
        userToken,
        matchLeadId,
        brandUid,
        trackPromise,
      );
      // A hard request failure already surfaced the global error snackbar —
      // leave the modal open as-is so the user can retry.
      if (!result) return;

      if (result.succeeded) {
        markLeadsReported([matchLeadId]);
        setToastMessage(`Lead reported to ${brandKey} successfully`);
        setToastSeverity("success");
        closeBrandMatch();
        refetchAfterLeadsChanged();
      } else {
        setToastMessage(result.message ?? "Something went wrong while reporting this lead to the brand");
        setToastSeverity("error");
      }
    } catch {
      // Belt-and-braces: reassignAndReportLead is designed to never throw
      // (it catches its own request/parsing errors), but if it somehow does,
      // the "Reporting…" state must not get stuck forever on this card.
      setToastMessage("Something went wrong while reporting this lead to the brand");
      setToastSeverity("error");
    } finally {
      setReportingBrandKey(null);
    }
  }

  function rerunBrandMatch() {
    if (!userToken || matchLeadId === null) return;
    LeadsManagementEndPointLogic.getBrandMatchData(
      userToken,
      websiteSharedUid,
      String(matchLeadId),
      true,
      trackPromise,
    );
  }

  function updateFilterAndResetPage(setter: (value: string) => void) {
    return (value: string) => {
      setter(value);
      setPage(1);
      setSelected({});
    };
  }

  function setPerPage(nextPerPage: number) {
    if (!isPageSizeOption(nextPerPage)) return;
    setPerPageState(nextPerPage);
    setPage(1);
    setSelected({});
  }

  return {
    leads,
    isLoading,
    canReportLeads,
    refetch: fetchLeads,
    view,
    setView,
    brand,
    start,
    end,
    search,
    status,
    sortField,
    sortDirection,
    toggleSort,
    page: currentPage,
    totalPages,
    perPage,
    setPerPage,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    pageStartIndex,
    totalFiltered,
    pageLeads,
    leadsForStatus: leads,
    leadsForAnalytics: leads,
    aggregations,
    allChecked,
    selectedCount,
    selectedLeads,
    detailLead,
    showConfirm,
    isReporting,
    toastMessage,
    toastSeverity,
    clearToast: () => setToastMessage(""),
    selected,
    brandOptions,
    getBrandUidByName,
    statusTabs: STATUS_TABS,
    qualified,
    ipFilter,
    minScore,
    multiBrand,
    setBrand: updateFilterAndResetPage(setBrand),
    setStart: updateFilterAndResetPage(setStart),
    setEnd: updateFilterAndResetPage(setEnd),
    setSearch: updateFilterAndResetPage(setSearch),
    setQualified: updateFilterAndResetPage(setQualifiedState),
    setIpFilter: updateFilterAndResetPage(setIpFilterState),
    setMinScore: updateFilterAndResetPage(setMinScoreState),
    setMultiBrand: updateFilterAndResetPage(setMultiBrandState),
    setStatus: (nextStatus: string) => {
      setStatus(resolveStatusTab(nextStatus));
      setPage(1);
      setSelected({});
    },
    goToFilteredLeads,
    toggleLeadSelected,
    toggleAllSelectable,
    clearSelection,
    openLeadDetail: (leadId: number) => setDetailId(leadId),
    closeLeadDetail: () => setDetailId(null),
    reportSingleLead,
    requestBulkReport: () => setShowConfirm(true),
    requestReportLead,
    cancelBulkReport: () => setShowConfirm(false),
    confirmBulkReport,
    allSelectedRejectable,
    allSelectedReportable,
    canRejectLeads: canReportLeads && allSelectedRejectable,
    showRejectConfirm,
    isRejecting,
    requestBulkReject: () => setShowRejectConfirm(true),
    requestRejectLead,
    cancelBulkReject: () => setShowRejectConfirm(false),
    confirmBulkReject,
    goToPrevPage: () => setPage((current) => Math.max(1, current - 1)),
    goToNextPage: () => setPage((current) => Math.min(totalPages, current + 1)),
    goToPage: (target: number) => setPage(Math.min(Math.max(1, target), totalPages)),
    matchLeadId,
    matchModalOpen,
    matchLead,
    matchLoading: matchLeadId !== null ? !!brandMatchLoading[matchLeadId] : false,
    matchError: matchLeadId !== null ? brandMatchError[matchLeadId] ?? null : null,
    matchDiagnostic,
    openBrandMatch,
    closeBrandMatch,
    onMatchModalExited: handleMatchModalExited,
    rerunBrandMatch,
    // A lead already reported successfully has nothing left to report — the
    // modal hides "Report to this brand" entirely once status is "Success".
    // Also gated by canReportLeads (edit_lead_managment) — same permission
    // check every other report action in this module respects (bulk report
    // bar, bulk report confirm, single-lead report in the detail drawer).
    canReportToBrand:
      BRAND_MATCH_REPORT_TO_BRAND_ENABLED && canReportLeads && matchLead?.status !== "Success",
    reportToBrand,
    reportingBrandKey,
  };
}

export type LeadsManagementData = ReturnType<typeof useLeadsManagementData>;
